import JobLogOutputHeader from "~/components/job/Job/Log/Output/Header";
import JobLogOutputLine from "~/components/job/Job/Log/Output/Line";
import debounce from "lodash/debounce";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import JobLogGroupStore from "~/stores/JobLogGroupStore";
import { Group, Job } from "~/stores/JobLogStore";
import {
  RunningDurationProvider,
  runningDurationProps,
  GROUP_NAME_CLASS,
  LINE_CONTENT_CLASS,
} from "~/components/shared/Log";
import {
  defaultRangeExtractor,
  Range as VirtualRange,
  useVirtualizer,
  Virtualizer,
} from "@tanstack/react-virtual";
import { useLineNumber } from "~/components/build/Show/lib/useLineNumber";
import { LogSearchContext } from "~/components/job/Job/Log/LogSearch";
import { useTestFailureMarkers } from "~/components/job/Job/Log/useTestFailureMarkers";
import { useFailureMarkerScroll } from "~/components/job/Job/Log/Output/useFailureMarkerScroll";
import {
  computeActiveLogGroupIndex,
  useGroupStickyMeta,
  useStickyLogGroupHeader,
} from "~/components/job/Job/Log/Output/useStickyLogGroupHeader";

// Floor for the scroll-padding that keeps scroll-to targets clear of the pinned
// group header (roughly one log line). The actual padding tracks the tallest
// measured group header so it also clears headers whose names wrap to multiple
// lines — see `stickyHeaderHeight` below.
const STICKY_HEADER_CLEARANCE = 28;

// Bounds how many times a pending scroll-to-match re-aims while waiting for the match's row to
// render (row offsets can shift as estimated heights are replaced by measurements). Prevents
// scroll ping-pong if a match can never be resolved to a DOM Range.
const MAX_MATCH_SCROLL_ATTEMPTS = 5;

export type LogLine = Group["lines"][number] & {
  type?: "line";
};

export type GroupLogLine = Omit<Group, "lines"> & {
  type: "group";
};

interface Match {
  /**
   * The text of the match.
   */
  match: string;

  /**
   * The log line number of the match.
   */
  line: number;

  /**
   * The character offset of the match within the line's flattened text content.
   */
  offset: number;

  /**
   * The log line group ID of the match.
   */
  group?: string;

  /**
   * The position of the match within the entire search result.
   */
  position: number;
}

interface HighlightMatch extends Match {
  /**
   * The range of the match in the text node.
   */
  range: Range;
}

/**
 * Find all the matches within current virtualised range.
 *
 * For each of these matches, find the text node that contains the match and create a range object
 * which can be used to highlight the match.
 */
export const getVirtualMatches = (
  virtualizer: Virtualizer<any, any>,
  matches: Match[],
  lines: Array<LogLine | GroupLogLine>,
): HighlightMatch[] => {
  if (!matches.length) {
    return [];
  }

  const { startIndex } = virtualizer.range || {};
  if (startIndex === undefined) {
    return [];
  }

  // Get the index of the first match within the range.
  const line = lines[startIndex];
  const matchIndexOffset = matches.findIndex((match) => match.line >= line.number);

  // No matches within this range.
  if (matchIndexOffset === -1) {
    return [];
  }

  // Get the virtual rows that are visible in the viewport, starting from the same line we've found
  // the first match in.
  const visibleItems = virtualizer
    .getVirtualItems()
    .filter((item) => (item.key as number) >= line.number);

  // Keep track of match index so we know where the match lives in the entire range.
  let matchIndex = matchIndexOffset;
  const highlights: HighlightMatch[] = [];

  // A match is located against a line's flattened text content, but coloured output splits that
  // text across multiple DOM text nodes (one per `<span>`). So for each row we concatenate its text
  // nodes into a single string, locate matches in that string, then map the offsets back to the
  // underlying nodes. This lets a single match span a colour boundary across multiple text nodes.
  for (const item of visibleItems) {
    const el = virtualizer.elementsCache.get(item.key);
    if (!(el instanceof Element)) {
      continue;
    }

    // Only walk the rendered log content (line content and group-header name), not the surrounding
    // UI chrome. Rows also contain hidden text — line-number tooltips ("Select line N"), the actions
    // menu trigger tooltip ("Line N actions"), etc. — rendered inline as `popover` elements. That
    // text is never part of the line's `textContent` used to build `matches`, so walking the whole
    // row injects phantom characters that collide with the search term (e.g. the "a" in "actions")
    // and desync the sequential match-to-range mapping below.
    const contentRoots = el.querySelectorAll(`.${LINE_CONTENT_CLASS}, .${GROUP_NAME_CLASS}`);

    // Collect the row's text nodes alongside their offset within the concatenated row text.
    const segments: { node: Node; start: number; end: number }[] = [];
    let combined = "";

    contentRoots.forEach((root) => {
      const treeWalker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
      let currentNode = treeWalker.nextNode();
      while (currentNode) {
        const text = currentNode.textContent ?? "";
        segments.push({
          node: currentNode,
          start: combined.length,
          end: combined.length + text.length,
        });
        combined += text;
        currentNode = treeWalker.nextNode();
      }
    });

    if (!combined) {
      continue;
    }

    // Find the node (and offset within it) that contains a given position in the combined text.
    const locate = (position: number) => {
      const segment = segments.find((seg) => position >= seg.start && position < seg.end);
      // `position` can equal the combined length when it's the end of the final match; clamp to the
      // last segment so the resulting range ends at the end of that node.
      const target = segment ?? segments[segments.length - 1];
      return { node: target.node, offset: position - target.start };
    };

    // Consume sequential matches as long as they occur within this row's text. A match belonging to
    // a later row won't be found here, so we stop and move to the next row without advancing past it.
    let searchPos = 0;
    while (matchIndex < matches.length) {
      const search = matches[matchIndex]?.match;
      if (!search) {
        break;
      }

      const startGlobal = combined.indexOf(search, searchPos);
      if (startGlobal === -1) {
        break;
      }
      const endGlobal = startGlobal + search.length;

      const { node: startNode, offset: startOffset } = locate(startGlobal);
      const { node: endNode, offset: endOffset } = locate(endGlobal);

      const range = new Range();
      range.setStart(startNode, startOffset);
      range.setEnd(endNode, endOffset);

      highlights.push({ ...matches[matchIndex], range });
      matchIndex++;
      searchPos = endGlobal;
    }
  }

  return highlights;
};

/**
 * Collect the text nodes a range spans, in document order, inclusive of the start and end
 * containers (which are always text nodes for the ranges produced by getVirtualMatches).
 */
const textNodesInRange = (range: Range): Text[] => {
  const startNode = range.startContainer;
  const endNode = range.endContainer;

  if (startNode === endNode && startNode.nodeType === Node.TEXT_NODE) {
    return [startNode as Text];
  }

  const nodes: Text[] = [];
  const treeWalker = document.createTreeWalker(range.commonAncestorContainer, NodeFilter.SHOW_TEXT);

  let node = treeWalker.nextNode();
  let collecting = false;
  while (node) {
    if (node === startNode) {
      collecting = true;
    }
    if (collecting) {
      nodes.push(node as Text);
    }
    if (node === endNode) {
      break;
    }
    node = treeWalker.nextNode();
  }

  return nodes;
};

/**
 * Wrap a (possibly cross-node) range in `.search-match` span(s) for browsers without the CSS Custom
 * Highlight API.
 *
 * `Range.surroundContents` throws when a range partially selects a non-text node, which is exactly
 * what a match spanning a colour `<span>` boundary does. To avoid this we split the range into
 * per-text-node slices and wrap each one independently, keeping each slice inside its own colour
 * span so the colours are preserved.
 */
export const applyFallbackHighlight = (range: Range, isActive: boolean): void => {
  const startNode = range.startContainer;
  const endNode = range.endContainer;
  const startOffset = range.startOffset;
  const endOffset = range.endOffset;

  // Snapshot the spanned nodes before mutating, since wrapping splits text nodes as we go.
  for (const node of textNodesInRange(range)) {
    const from = node === startNode ? startOffset : 0;
    const to = node === endNode ? endOffset : node.length;
    if (from >= to) {
      continue;
    }

    const slice = document.createRange();
    slice.setStart(node, from);
    slice.setEnd(node, to);

    const highlight = document.createElement("span");
    highlight.classList.add("search-match");
    if (isActive) {
      highlight.classList.add("search-match--active");
    }

    // Each slice is contained within a single text node, so this never partially selects an element.
    slice.surroundContents(highlight);
  }
};

/**
 * Centre a match's Range in the log viewport.
 *
 * Row-level scrolling (`scrollToIndex`) can't target a match inside a wrapped log line: every
 * match within the line shares one virtual row, which can be many viewports tall. Measuring the
 * Range's rect gives the match's exact position within the row.
 *
 * Skips scrolling when the match is already fully visible (mirroring browser find behaviour), with
 * `clearanceTop` accounting for the pinned group header at the top of the viewport.
 *
 * Scrolls via the virtualizer (not `scrollTop` directly) so a pending `scrollToIndex` retry — the
 * virtualizer re-issues index scrolls after dynamic measurement — is cancelled rather than left to
 * fire afterwards and yank the viewport back to the row-level target.
 */
export const scrollRangeIntoView = (
  virtualizer: Virtualizer<any, any>,
  range: Range,
  clearanceTop = 0,
): void => {
  const scrollElement = virtualizer.scrollElement as Element | null;
  if (!scrollElement) {
    return;
  }

  const rect = range.getBoundingClientRect();
  const containerRect = scrollElement.getBoundingClientRect();

  if (rect.top >= containerRect.top + clearanceTop && rect.bottom <= containerRect.bottom) {
    return;
  }

  const offset = scrollElement.scrollTop + (rect.top - containerRect.top) + rect.height / 2;
  virtualizer.scrollToOffset(offset, { align: "center" });
};

function escapeRegex(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Track text selection within a container element.
 * Returns the range of selected virtualizer indices as [minIndex, maxIndex], or null if no selection.
 */
function useSelectedRange(containerRef: React.RefObject<HTMLElement | null>) {
  const [selectedRange, setSelectedRange] = useState<[number, number] | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    // Debounce the state update to avoid performance issues during rapid selection changes
    const debouncedSetRange = debounce((range: [number, number] | null) => {
      setSelectedRange(range);
    }, 150);

    const handleSelectionChange = () => {
      const selection = document.getSelection();
      if (!selection || selection.isCollapsed) {
        debouncedSetRange(null);
        return;
      }

      const anchorNode = selection.anchorNode;
      const focusNode = selection.focusNode;

      if (!anchorNode || !focusNode) {
        return;
      }

      // Ignore selections outside our container
      if (!container.contains(anchorNode) || !container.contains(focusNode)) {
        return;
      }

      // Find the virtualizer indices for the selection endpoints
      const findRowIndex = (node: Node): number | null => {
        let current: Node | null = node;
        while (current && current !== container) {
          if (current instanceof HTMLElement) {
            const index = current.dataset.index;
            if (index !== undefined) {
              return parseInt(index, 10);
            }
          }
          current = current.parentNode;
        }
        return null;
      };

      const startIndex = findRowIndex(anchorNode);
      const endIndex = findRowIndex(focusNode);

      if (startIndex === null || endIndex === null) {
        return;
      }

      const minIndex = Math.min(startIndex, endIndex);
      const maxIndex = Math.max(startIndex, endIndex);

      debouncedSetRange([minIndex, maxIndex]);
    };

    document.addEventListener("selectionchange", handleSelectionChange);

    return () => {
      document.removeEventListener("selectionchange", handleSelectionChange);
      debouncedSetRange.cancel();
    };
  }, [containerRef]);

  return selectedRange;
}

/**
 * A virtualised table of job log lines and group headers where only the lines that are
 * visible within the viewport are rendered.
 */
export const VirtualisedLogOutput = ({
  log,
  timestampsOn,
  onTimestampClick,
  timestampsInUTC,
  following,
  onDisableFollow,
  onFailureMarkersChange,
}: {
  log: Job;
  timestampsOn: boolean | null | undefined;
  timestampsInUTC?: boolean | null | undefined;
  onTimestampClick: () => void;
  following?: boolean;
  onDisableFollow?: () => void;
  onFailureMarkersChange?: (
    info: { count: number; current?: number; scrollToNext: () => number } | null,
  ) => void;
}) => {
  const [expandedLogGroups, setExpandedLogGroups] = useState({
    ...JobLogGroupStore.state,
  });

  /**
   * Update internal state when any log groups change to re-calculate the lines to be displayed.
   *
   * Ideally we move away from event listeners and instead use a zustand store once we've phased out the
   * legacy stores.
   */
  useEffect(() => {
    const handleGroupChange = () => {
      setExpandedLogGroups({ ...JobLogGroupStore.state });
    };

    log.groups.forEach((group) => {
      JobLogGroupStore.addChangeListener(group.id, handleGroupChange);
      JobLogGroupStore.setDefault(group.id, group.expanded);
      setExpandedLogGroups({ ...JobLogGroupStore.state });
    });

    return () => {
      log.groups.forEach((group) => {
        JobLogGroupStore.removeChangeListener(group.id, handleGroupChange);
      });
    };
  }, [log.groups]);

  /**
   * Flatten log groups into an array of log lines and group headers to make virtualisation easier.
   */
  const lines = useMemo(() => {
    return log.groups.reduce<Array<LogLine | GroupLogLine>>((acc, group) => {
      const { lines, ...groupLine } = group;

      if (group.name) {
        acc.push({ ...groupLine, type: "group" });
      }

      if (expandedLogGroups[group.id] && lines?.length) {
        acc = acc.concat(group.lines);
      }
      return acc;
    }, []);
  }, [log.groups, expandedLogGroups]);

  const { markers, loading: markersLoading } = useTestFailureMarkers(log);

  const tableRef = useRef<HTMLTableElement>(null);

  const groupStickyMeta = useGroupStickyMeta(lines);

  /**
   * Track the range of selected lines so we can keep them rendered even when scrolled out of view.
   * This prevents text selection from being truncated when the user scrolls during selection.
   */
  const selectedRange = useSelectedRange(tableRef);

  const getItemKey = useCallback((index: number) => lines[index].number, [lines]);

  // Scroll-padding used so `scrollToIndex(..., { align: "start" })` (hash/line
  // navigation, failure markers) never lands a row underneath the pinned group
  // header. Tracks the tallest group header (see the effect below) so it also
  // clears multi-line headers, not just the single-line `STICKY_HEADER_CLEARANCE`.
  const [stickyHeaderHeight, setStickyHeaderHeight] = useState(STICKY_HEADER_CLEARANCE);

  const exampleCharRef = useRef<HTMLSpanElement>(null);

  /**
   * Custom range extractor that includes selected lines in the rendered range.
   * This ensures that text selection is preserved even when scrolling beyond the virtual window.
   */
  const rangeExtractor = useCallback(
    (range: VirtualRange) => {
      const defaultRange = defaultRangeExtractor(range);

      // Pin the active group header: recompute it from the current scroll
      // position so the pinned row is always kept in the rendered range (it can
      // be far above the visible window). Computed independently of the hook's
      // state — they derive the same value from the same scroll offset.
      const activeStickyIndex = computeActiveLogGroupIndex(
        rowVirtualizer.scrollOffset ?? 0,
        0,
        groupStickyMeta.groupIndices,
        groupStickyMeta.eligibleByIndex,
        rowVirtualizer.measurementsCache,
      );

      const hasSticky = activeStickyIndex >= 0 && !defaultRange.includes(activeStickyIndex);

      if (defaultRange.length === 0) {
        return hasSticky ? [activeStickyIndex] : defaultRange;
      }

      // Fold in selected lines (kept rendered so scrolling mid-selection
      // doesn't truncate the selection) and the pinned header, deduping
      // against the visible range.
      const extra = new Set<number>();
      if (hasSticky) {
        extra.add(activeStickyIndex);
      }

      if (selectedRange) {
        const selectionStart = Math.max(0, selectedRange[0]);
        const selectionEnd = Math.min(selectedRange[1], range.count - 1);
        const visibleStart = defaultRange[0];
        const visibleEnd = defaultRange[defaultRange.length - 1];

        for (let index = selectionStart; index <= selectionEnd; index++) {
          if (index < visibleStart || index > visibleEnd) {
            extra.add(index);
          }
        }
      }

      if (extra.size === 0) {
        return defaultRange;
      }

      return [...extra, ...defaultRange].sort((left, right) => left - right);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- rowVirtualizer is stable; activeStickyIndexRef is a ref
    [selectedRange, groupStickyMeta],
  );

  const rowVirtualizer = useVirtualizer({
    count: lines.length,

    getItemKey,

    // The log table is itself the scroll viewport (`display: block; overflow: auto`),
    // so the toolbar and footer sit outside the scrolling region.
    getScrollElement: useCallback((): Element | null => tableRef.current, []),

    // Callback to estimate the size of each row, calculated based on the width of a single character. This doesn't have to be
    // perfect as the virtualizer will measure the actual size of each row when it's rendered. It does however need to be roughly
    // in the ballpark as it helps with initial rendering performance and reduces the number of re-renders which in some cases
    // can cause React to throw an error.
    estimateSize: useCallback(
      (index) => {
        const charWidth = exampleCharRef.current?.clientWidth || 0;
        const charHeight = exampleCharRef.current?.clientHeight || 0;
        const defaultHeight = 36; // Default height for log lines (roughly 2x the height of a single line of text).

        // For log lines, we can estimate the height based on the text content.
        if (tableRef.current && charWidth && charHeight) {
          const line = lines[index];
          const textLength = line.textContent.length;
          const estimatedHeight =
            Math.ceil(textLength / (tableRef.current.clientWidth / charWidth)) * charHeight;

          return estimatedHeight;
        }

        return charHeight || defaultHeight;
      },
      [lines],
    ),

    // Clearance for the pinned group header so search/hash navigation never
    // lands a line underneath it. Sized to the tallest measured header so it
    // also covers headers whose names wrap to multiple lines.
    scrollPaddingStart: stickyHeaderHeight,

    // Render overscan rows to avoid flickering when scrolling.
    overscan: 10,

    rangeExtractor,
  });

  // Forces a re-render when the pinned header flips (so the render-time
  // computation below re-runs even when the virtualizer's range is unchanged)
  // and drives the push offset on the pinned header.
  useStickyLogGroupHeader({
    rowVirtualizer,
    meta: groupStickyMeta,
    // The table itself is the scroll viewport (the toolbar sits outside it),
    // so headers pin flush with its top edge.
    offsetTop: 0,
  });

  // Get the current search term and position from the context.
  const { search, setTotalMatches, position, caseSensitive } = useContext(LogSearchContext);

  /**
   * Find all the matches in the log lines and group headers
   */
  const { matches, groups } = useMemo(() => {
    const groups = new Set<string>();

    if (!search) {
      return { matches: [], groups };
    }

    // TODO: This is the spot to change to support regex searching, probably
    // by accepting a flag to switch between regex and non-regex searching.
    const regex = new RegExp(escapeRegex(search), caseSensitive ? "g" : "gi");
    const matchText = (text: string) => {
      return Array.from(text.matchAll(regex), (match) => ({
        match: match[0],
        offset: match.index ?? 0,
      }));
    };

    let position = 0;
    const matches = log.groups.reduce<Match[]>((acc, group) => {
      acc.push(
        ...matchText(group.textContent).map((match) => ({
          ...match,
          line: group.number,
          position: ++position,
        })),
      );

      group.lines.forEach((line) => {
        const lineMatches = matchText(line.textContent);
        if (lineMatches.length) {
          acc.push(
            ...lineMatches.map((match) => ({
              ...match,
              line: line.number,
              position: ++position,
              group: group.id,
            })),
          );
          groups.add(group.id);
        }
      });

      return acc;
    }, []);

    return { matches, groups };
    // `log.groups` is a dependency because it changes as streaming logs are
    // re-parsed: matches and the set of groups containing them must be
    // recomputed so an active search stays current with new output.
  }, [search, caseSensitive, log.groups]);

  /**
   * Expand all the groups with matches in them. Recomputing `groups` on each
   * re-parse (above) also re-asserts this after other expanders run — e.g. the
   * running-group auto-expand reverting a group when the job moves on — so an
   * active search keeps its matches visible. We skip groups that are already
   * expanded to avoid redundant store writes (and change events) every poll.
   */
  useEffect(() => {
    for (const group of groups) {
      if (JobLogGroupStore.isExpanded(group) !== true) {
        JobLogGroupStore.set(group, true);
      }
    }
  }, [groups]);

  /**
   * A scroll-to-match that couldn't be completed precisely yet because the match's row hasn't
   * rendered. Completed (or re-aimed) by the highlight effect as the virtual window changes.
   */
  const pendingMatchScroll = useRef<{ position: number; attempts: number } | null>(null);

  /**
   * Precisely centre a match in the viewport by measuring its DOM Range. Only possible once the
   * match's row is rendered; returns false when it isn't.
   */
  const scrollToMatchRange = useCallback(
    (targetPosition: number, virtualMatches: HighlightMatch[]) => {
      const highlight = virtualMatches.find((match) => match.position === targetPosition);
      if (!highlight) {
        return false;
      }

      scrollRangeIntoView(rowVirtualizer, highlight.range, stickyHeaderHeight);
      return true;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- rowVirtualizer is stable
    [stickyHeaderHeight],
  );

  /**
   * Roughly scroll towards a match whose row hasn't rendered yet: aim at the match's proportional
   * character offset within its row, so a wrapped log line taller than the viewport lands near
   * the match rather than at the row's midpoint. The precise correction happens once the row
   * renders and the match's Range can be measured (see the highlight effect).
   */
  const scrollTowardsMatch = useCallback(
    (match: Match) => {
      const virtualIndex = lines.findIndex((line) => line.number === match.line);
      if (virtualIndex === -1) {
        return;
      }

      const item = rowVirtualizer.measurementsCache[virtualIndex];
      const textLength = lines[virtualIndex].textContent.length;

      if (item && textLength > 0) {
        rowVirtualizer.scrollToOffset(item.start + item.size * (match.offset / textLength), {
          align: "center",
        });
      } else {
        rowVirtualizer.scrollToIndex(virtualIndex, { align: "center" });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- rowVirtualizer is stable
    [lines],
  );

  /**
   * Highlight the search results when the search term or virtualised rows change.
   */
  useEffect(() => {
    clearHighlights();

    const virtualMatches = getVirtualMatches(rowVirtualizer, matches, lines);
    if (!matches.length) {
      return;
    }

    // Complete (or keep converging on) a pending scroll-to-match now that the virtual window has
    // changed. This runs before highlights are applied because the fallback highlighter mutates
    // text nodes, which would invalidate the Ranges being measured.
    const pending = pendingMatchScroll.current;
    if (pending) {
      if (pending.position !== position) {
        // Stale: the user has moved on to another match or search.
        pendingMatchScroll.current = null;
      } else if (scrollToMatchRange(pending.position, virtualMatches)) {
        pendingMatchScroll.current = null;
      } else if (pending.attempts < MAX_MATCH_SCROLL_ATTEMPTS) {
        pending.attempts += 1;
        const match = matches[pending.position - 1];
        if (match) {
          scrollTowardsMatch(match);
        }
      } else {
        pendingMatchScroll.current = null;
      }
    }

    highlightMatches(virtualMatches, position);
  }, [matches, rowVirtualizer.range, position, lines]);

  /**
   * Clear all the highlights in the log lines.
   */
  const clearHighlights = useCallback(() => {
    if (CSS.highlights) {
      CSS.highlights.clear();
    } else {
      // Fallback for browsers that don't support the CSS Highlight API (ie. Firefox)
      rowVirtualizer.scrollElement?.querySelectorAll(".search-match").forEach((el) => {
        const parent = el.parentNode;
        if (parent) {
          parent.replaceChild(document.createTextNode(el.textContent || ""), el);
          parent.normalize();
        }
      });
    }
  }, [rowVirtualizer.scrollElement]);

  /**
   * Highlight search matches in the log lines.
   */
  const highlightMatches = useCallback((matches: HighlightMatch[], currentPosition: number) => {
    if (CSS.highlights) {
      CSS.highlights.set("search-match", new Highlight(...matches.map((match) => match.range)));
      const active = matches.find((match) => match.position === currentPosition);
      if (active) {
        CSS.highlights.set("search-match--active", new Highlight(active.range));
      }
    } else {
      // Fallback for browsers that don't support the CSS Highlight API (ie. Firefox)
      for (const match of matches) {
        applyFallbackHighlight(match.range, match.position === currentPosition);
      }
    }
  }, []);

  /**
   * Reset the current match when the search term changes.
   */
  useEffect(() => {
    setTotalMatches(matches.length);
  }, [matches.length, search]);

  /**
   * Scroll to the current match position when it changes.
   *
   * A row-level scroll isn't precise enough on its own: a wrapped log line renders as a single
   * virtual row that can be many viewports tall, with each of its matches at a different offset
   * inside it. When the match's row is already rendered, its exact Range is measured and centred
   * immediately; otherwise aim for the match's estimated position within its row and let the
   * highlight effect complete the precise scroll once the row renders.
   */
  useEffect(() => {
    if (!matches.length || position === 0) {
      return;
    }

    const match = matches[position - 1];
    if (!match) {
      return;
    }

    if (scrollToMatchRange(position, getVirtualMatches(rowVirtualizer, matches, lines))) {
      pendingMatchScroll.current = null;
      return;
    }

    pendingMatchScroll.current = { position, attempts: 0 };
    scrollTowardsMatch(match);
  }, [position, matches]);

  /**
   * When a group/line number exists in the URL hash, scroll to it, and update the scroll
   * position if the URL hash changes
   */
  const { group, lineNumber } = useLineNumber();
  const logLineNumber = lineNumber ?? group;

  // Since lines vary based on expanded/collapsed groups, we need to hook into `lines` in our useEffect
  // below which means it can fire multiple times. To avoid repeated scrolling, keep track of the previous scroll
  // position and bail if the number hasn't changed.
  const prevScrollNumber = useRef<number | null>(null);

  useEffect(() => {
    if (logLineNumber && logLineNumber !== prevScrollNumber.current) {
      const virtualIndex = lines.findIndex((line) => line.number === logLineNumber);

      // Only scroll if we actually found the line in the expanded groups
      if (virtualIndex !== -1) {
        // If we found the line, update the previous scroll number
        prevScrollNumber.current = logLineNumber;

        // Check if the line is already visible in the viewport
        const { startIndex, endIndex } = rowVirtualizer.range || {};
        const isLineVisible =
          startIndex !== undefined &&
          endIndex !== undefined &&
          virtualIndex >= startIndex &&
          virtualIndex <= endIndex;

        // Only scroll if the line is not already visible
        if (!isLineVisible) {
          // Small delay to allow the DOM to update before scrolling
          setTimeout(() => {
            rowVirtualizer.scrollToIndex(virtualIndex, { align: "start" });
          }, 0);
        }
      }
    }
  }, [logLineNumber, lines]);

  const { failureLineNumbers } = useFailureMarkerScroll({
    markers,
    markersLoading,
    lines,
    log,
    logLineNumber,
    following,
    onDisableFollow,
    onFailureMarkersChange,
    rowVirtualizer,
  });

  // Compute the pinned header at render time from the current scroll offset,
  // so the row rendered as `position: sticky` is always correct for this frame
  // (no lag from a state value that updates a tick late). `getVirtualItems()`
  // first to ensure the measurement cache is fresh for the scan below.
  const virtualItems = rowVirtualizer.getVirtualItems();
  const activeStickyIndex = computeActiveLogGroupIndex(
    rowVirtualizer.scrollOffset ?? 0,
    0,
    groupStickyMeta.groupIndices,
    groupStickyMeta.eligibleByIndex,
    rowVirtualizer.measurementsCache,
  );

  // Safe upper bound for the pinned-header scroll-padding: the tallest group
  // header. The virtualizer's measurement cache already holds a size for every
  // group header (the real measured height once rendered, otherwise a
  // wrap-aware estimate from `estimateSize`), so this reuses existing
  // measurements — no extra DOM reads. Group headers are few, so the scan is
  // cheap, and the effect only writes state when the bound actually changes.
  let tallestGroupHeader = STICKY_HEADER_CLEARANCE;
  for (const index of groupStickyMeta.groupIndices) {
    const size = rowVirtualizer.measurementsCache[index]?.size ?? 0;
    if (size > tallestGroupHeader) {
      tallestGroupHeader = size;
    }
  }

  useEffect(() => {
    setStickyHeaderHeight((current) =>
      current === tallestGroupHeader ? current : tallestGroupHeader,
    );
  }, [tallestGroupHeader]);

  const lineNumberWidth = log.totalLines?.toString().length ?? 0;
  const tableStyle = {
    "--line-number-width": `${Math.max(lineNumberWidth, 2)}ch`,
  } as React.CSSProperties;

  // The tbody holds the full virtualised height and is the positioned ancestor
  // for the absolutely-positioned rows; the table above it is the scroll viewport.
  const bodyStyle = {
    height: `${rowVirtualizer.getTotalSize()}px`,
    position: "relative",
  } as React.CSSProperties;

  return (
    <RunningDurationProvider {...runningDurationProps(log.groups)}>
      <span className="invisible absolute font-mono" ref={exampleCharRef}>
        A
      </span>
      <table
        ref={tableRef}
        className="JobLogOutputComponent__Table block min-h-0 flex-1 overflow-auto"
        style={tableStyle}
        cellPadding="0"
      >
        <tbody className="block" style={bodyStyle}>
          {virtualItems.map((virtualItem) => {
            const line = lines[virtualItem.index];
            const isStickyHeader = line.type === "group" && virtualItem.index === activeStickyIndex;

            const virtualizerProps = {
              "data-index": virtualItem.index,
              style: isStickyHeader
                ? ({
                    // Pin to the top of the table viewport; the push offset (a
                    // negative delta written to the CSS var by the scroll
                    // listener) slides the header up as the next group header
                    // arrives (iOS-contacts style). Driven through `top` rather
                    // than a transform so anchor positioning on the line-number
                    // dropdown still resolves in Firefox and Safari (see the
                    // absolute branch).
                    position: "sticky",
                    top: "var(--sticky-push-offset, 0px)",
                    width: "100%",
                    left: 0,
                    // Above scrolling log lines, below the toolbar (z-10).
                    zIndex: 4,
                  } as React.CSSProperties)
                : ({
                    position: "absolute",
                    width: "100%",
                    left: 0,
                    // Use `top` instead of `transform: translateY(...)` so anchor
                    // positioning (used by tooltips on line numbers) resolves
                    // correctly in Firefox and Safari, which don't account for
                    // ancestor transforms when computing anchor() values.
                    top: virtualItem.start,
                  } as React.CSSProperties),
            };

            switch (line.type) {
              case "group":
                return (
                  <JobLogOutputHeader
                    key={virtualItem.key}
                    innerRef={rowVirtualizer.measureElement}
                    className={isStickyHeader ? "JobLogOutputComponent__Header--Pinned" : undefined}
                    timestampsOn={timestampsOn}
                    timestampsInUTC={timestampsInUTC}
                    onTimestampClick={onTimestampClick}
                    durationPercentage={line.durationPercentage}
                    maxDuration={line.maxDuration}
                    startedAt={line.startedAt}
                    finishedAt={line.finishedAt}
                    name={line.name}
                    id={line.id}
                    jobId={log.id}
                    number={line.number}
                    finished={line.finished}
                    deemphasized={line.deemphasized}
                    {...virtualizerProps}
                  />
                );
              default:
                return (
                  <JobLogOutputLine
                    key={virtualItem.key}
                    innerRef={rowVirtualizer.measureElement}
                    timestampsOn={timestampsOn}
                    timestampsInUTC={timestampsInUTC}
                    onTimestampClick={onTimestampClick}
                    id={line.id}
                    jobId={log.id}
                    number={line.number}
                    line={line.line}
                    startedAt={line.startedAt}
                    failureMarker={failureLineNumbers.get(line.number)}
                    {...virtualizerProps}
                  />
                );
            }
          })}
        </tbody>
      </table>
    </RunningDurationProvider>
  );
};
